import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import './CertificatePaymentPreview.scss'
import { ICertificatePaymentPreview } from 'app_types/user/certificateTypes';
import CertificatePaymentPreviewCertificateInformation from './components/CertificatePaymentPreviewCertificateInformation/CertificatePaymentPreviewCertificateInformation';
import { useForm } from 'react-hook-form';
import CertificateService from 'app_services/user/certificateService';
import { EfipayCurrency } from 'app_types/efipay/efipayTypes';
import ErpService from 'app_services/erp/erpService';
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper';
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { ERPClassification, ERPNature } from 'app_types/erp/erpTypes';

interface ICertificatePaymentPreviewProps {
  data: ICertificatePaymentPreview
  onClose: () => void
}

interface IHandleSubmitParams {
  fullName: string
  docNumber: string
  nature: string
  classification: string
  currency: EfipayCurrency
  country: {
    label: string
    value: string
  }
  department: {
    label: string
    value: string
  }
  city: {
    label: string
    value: string
  }
}

const CertificatePaymentPreview: FC<ICertificatePaymentPreviewProps> = ({data, onClose}) => {
  const formHook = useForm({ defaultValues: {
    ...data,
    nature: data?.nature ? data?.nature : ERPNature.NATURAL,
    classification: data?.classification ? data?.classification : ERPClassification.NO_RESPONSABLE,
    currency: EfipayCurrency.COP
  }})
  const {control, errors, handleSubmit, watch} = formHook
  const currentNature = watch('nature')
  const certificateService = useMemo(() => new CertificateService(), [])
  const erpService = useMemo(() => new ErpService(), [])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [certificatePrice, setCertificatePrice] = useState<Record<EfipayCurrency, number>>()
  const [priceLoading, setPriceLoading] = useState<boolean>(false)

  const handleClickPayment = useCallback(async ({
    docNumber,
    fullName,
    classification = ERPClassification.NO_RESPONSABLE,
    nature,
    country,
    department,
    city,
    currency,
  }: IHandleSubmitParams) => {
    setIsLoading(true)
    const response = await certificateService.certificatePayment({
      certificateQueueId: data.certificate._id,
      currencyType: currency,
      certificateInfo: {
        fullName,
        docNumber,
        classification,
        nature,
        country: country?.value,
        department: department?.value,
        city: city?.value,
      }
    })
    if (response?.code === 200) {
      const url = response?.redirectUrl
      window.open(url, '_self', 'noreferrer')
    }
    setIsLoading(false)
  }, [certificateService, data.certificate._id])

  const fetchCertificatePrice = useCallback(async () => {
    setPriceLoading(true)
    const response = await erpService.getCertificatePriceFromCertificateQueue(data.certificate._id)
    if (response?.code === 200 && response?.price) {
      setCertificatePrice(response.price)
    } else {
      setCertificatePrice(undefined)
    }
    setPriceLoading(false)
  }, [data.certificate._id, erpService])

  const handleSubmitForm = (params: IHandleSubmitParams) => {
    handleClickPayment(params)
  }

  useEffect(() => {
    fetchCertificatePrice()
  }, [fetchCertificatePrice])

  const isValidPrice = certificatePrice?.[EfipayCurrency.COP] || certificatePrice?.[EfipayCurrency.USD]

  return (
    <div className='certificate-payment-preview--container'>
      <LoadingWrapper loading={priceLoading}>
        <>
          {isValidPrice && (
            <>
              <CertificatePaymentPreviewCertificateInformation
                data={data}
                errors={errors}
                control={control}
                progress='50%'
                onClose={onClose}
                onNext={() => {}}
                handleSubmit={handleSubmit(handleSubmitForm)}
                price={certificatePrice}
                isLoading={isLoading}
                currentNature={currentNature}
                formHook={formHook}
              />
            </>
          )}
          {!isValidPrice && !priceLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
              <TextComponent>
                Ha ocurrido un error, por favor inténtalo mas tarde.
              </TextComponent>
              <ButtonComponent onClick={onClose}>
                Cerrar
              </ButtonComponent>
            </div>
          )}
        </>
      </LoadingWrapper>
    </div>
  )
}

export default CertificatePaymentPreview
